import React, { useState, useEffect, useContext } from "react";
import Base from './base.jsx'; 
import { castDataType, DataType, Mode } from "../common.jsx";
import Helpers from './helpers.jsx'
import Setting from "./setting.jsx";

import Resolver from '../component_resolver.jsx'
import * as FormComponent from "../form_components.jsx";
import { Row, Col, Button } from "../../bootstrap/index.jsx";
import update from "immutability-helper";
import Tooltip from "../../common/Tooltip.jsx";
import { Parser } from 'hot-formula-parser';
import { parseFormula } from "../../../helpers/index.js";
import { useRemoteVariableValues } from "../../../hooks/index.js";
import { FormContext } from "../form_context.jsx";
import { NumberFormatting } from '../common.jsx';
import CommonColorPicker from "../../common/ColorPicker/CommonColorPicker.jsx";
import CommonSlider from "../../common/CommonSlider.jsx";

const CALCULATION_DELAY = 300;
const NO_DELAY = 0;

export default class AdaliaMeCalculation extends Base {
  static get CATEGORY() {
    return "other";
  }

  // Calculation can be done to any component
  static resolveRelatedAnswerNamesOrIds(content, repeatedFieldScope = "") {
    let names = [];
    if (content.name) {
      names.push(repeatedFieldScope + "[" + content.name + "]");
    }
    let variables = (content.variables || []).map((v) => repeatedFieldScope + "[" + v.componentId + "]");
    return [...variables, ...names];
  }

  static get TYPE() {
    return "adalia_me_calculation";
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: "calculation_default_label",
    };
  }

  getAvailableDataTypeOptions() {
    return [DataType.INTEGER, DataType.DECIMAL, DataType.STRING]
  }

  static icon() {
    return <i className="fa fa-calculator"></i>;
  }

  static deprecated() {
    // v2 is not yet fully compatible, use both for now
    return false;
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      label: "label",
      name: "field_" + Helpers.randomString(),
      dataType: DataType.DECIMAL,
      formula: "",
      variables: [
        {
          id: Helpers.randomString(),
          name: "a",
        },
      ],
      style_options: [
        {
          font_size: 14,
          color: "#000",
        },
      ],
      prettyView: {
          hide: false,
          show_label: false
      }
    });
  }

  static addVariable(content, v = {}) {
    return update(content, {
      variables: (arr) => {
        let i = 9;
        arr.push({
          id: Helpers.randomString(),
          name: [...Array(26)].map((_) => (++i).toString(36)).join``[arr.length],
          ...v,
        });

        return arr;
      },
    });
  }

  renderView() {
    const { answers, prefillAnswers, content, mode, findComponentsCallback, projectId, pdf, repeatedFieldScope } = this.props;
    const { formula, errorValue, variables = [], style_options = [] } = content;

    const defaultValue = this.getAnswer();

    return (
      <div>
        <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
          <span className="field-label">{this.getText('label')}</span>
          {pdf ? null : (
            <CalculationValue
              inputName={this.fullFieldName()}
              defaultValue={defaultValue}
              shouldDisplayDefaultValue={mode == Mode.VIEW}
              shouldCalculateValue={[Mode.FILL, Mode.EDIT].includes(mode)}
              shouldFetchVariableValues={mode == Mode.FILL}
              calculationDelay={mode == Mode.FILL ? CALCULATION_DELAY : NO_DELAY}
              shouldDisplayError={mode == Mode.EDIT}
              formula={formula}
              errorValue={errorValue}
              variables={variables}
              style_options={style_options}
              answers={answers}
              prefillAnswers={prefillAnswers}
              repeatedFieldScope={repeatedFieldScope}
              onValueChange={this.updateAnswer}
              findComponentsCallback={findComponentsCallback}
              projectId={projectId}
              createNewComponentInstance={this.createNewComponentInstance}
              reportKeysToFieldsFunctions={['EXTRACT_ROWS']}
            />
          )}
        </label>
        {pdf ? <div>{defaultValue}</div> : null}
      </div>
    );
  }

  renderPrettyView() {
    const defaultValue = this.getAnswer();
    const { hide = false, show_label = false } = this.props.content.prettyView || {};
    return (hide ? null :
         <div>
            {show_label &&
              <label>
                  <span className="field-label">{this.getText('label')}</span>
              </label>
            }
            {defaultValue}
          </div>
    );
  }

  renderSettings() {
    const numberFormattingOptions = [NumberFormatting.NONE, NumberFormatting.CURRENCY, NumberFormatting.NUMBER].map(formattingOption => ({
        key: formattingOption, 
        label: I18n.t('text_field_number_formatting_option_' + formattingOption, {scope: 'fe'})
      }
    ));

    const NumberFormattingRadioSelection = <Setting.RadioSelection 
        name="numberFormatting" 
        label={I18n.t('text_field_number_formatting', {scope: 'fe'})} 
        selectedOption={this.props.content.numberFormatting} 
        onChange={this.updateSettings} 
        options={numberFormattingOptions} 
      />
    return (
      <div>
        <Setting.TextField name="label" label={I18n.t('field_label', {scope: 'fe'})} value={this.getText("label")} onChange={this.updateSettings} />
        <Setting.Checkbox name="label_hidden" className="label-hidden-check" label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
        <Setting.TextField
          name="formula"
          label={I18n.t('field_formula', {scope: 'fe'})}
          placeholder={I18n.t('field_formula_placeholder', {scope: 'fe'})}
          value={this.props.content.formula}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
        <Setting.TextField
          name="errorValue"
          label={I18n.t('field_error_value', {scope: 'fe'})}
          placeholder={I18n.t('field_error_value_placeholder', {scope: 'fe'})}
          value={this.props.content.errorValue}
          onChange={this.updateSettings}
        />
        {this.renderVariableSettings()}
        <Setting.Dropdown name="dataType" label={I18n.t('field_data_type', {scope: 'fe'})} value={this.props.content.dataType} onChange={this.updateSettings} options={this.getAvailableDataTypeOptions()} />
        <div className='setting'>
          <label>
            {I18n.t('calculation_validations', {scope: 'fe'})}
          </label>
          <Setting.TextField name="validateEqual" onChange={this.updateSettings} value={this.props.content.validations?.equal} description={I18n.t('calculation_validations_description', {scope: 'fe'})} />
        </div>
        {NumberFormattingRadioSelection}
        {this.renderStylingSettings()}
        <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
        <Setting.Checkbox
          name="containsSensitiveInformation"
          label={I18n.t('field_contains_sensitive_information', {scope: 'fe'})}
          description={I18n.t('field_contains_sensitive_information_description', {scope: 'fe'})}
          value={this.props.content.containsSensitiveInformation}
          {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
        />
        {this.renderPrettyViewSettings()}
      </div>
    );
  }

  componentDidMount() {
    setTimeout(() => {
      this.componentDidUpdate();
    }, 0)
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate();

    const calculationDelay = this.props.mode == Mode.FILL ? CALCULATION_DELAY : NO_DELAY
    if (this.props.content && !isNaN(this.getRawAnswer()) && this.getRawAnswer() !== '' && this.props.content?.dataType !== DataType.STRING) {
      const input = this.refs.root.querySelector('input[type="text"]');

      if (this.props.content?.numberFormatting === NumberFormatting.CURRENCY) {
        setTimeout(() => {
          new Helpers.CurrencyFormatter(input, {
            format: document.body.getAttribute("data-currency-format"),
            readOnly: this.props.mode != Mode.FILL || this.isReadOnlyAnswer()
          });
        }, calculationDelay)
      }
      else if (this.props.content?.numberFormatting === NumberFormatting.NUMBER) {
        let format = document.body.getAttribute("data-number-format");

        if (format === '1 000.0…') format = 'default'
        else if (format === '1 000,0…') format = 'format_1'
        else if (format === '1,000.0…') format = 'format_2'
        else if (format === '1.000,0…') format = 'format_3'

        setTimeout(() => {
          new Helpers.CurrencyFormatter(input, {
            format: format,
            readOnly: this.props.mode != Mode.FILL || this.isReadOnlyAnswer()
          });
        }, calculationDelay)
      }
    }
  }

  renderVariableSettings() {
    let variableNames = [];
    let showVariableNameWarning = false;

    const variables = this.getVariables().map((v, i) => {
      if (v.name) {
        if (variableNames.includes(v.name)) {
          showVariableNameWarning = true;
        }
        variableNames.push(v.name);
      }

      return (
        <div key={v.id} className="variable-row mb-1">
          <input type="hidden" name={`variables|${i}|id`} defaultValue={v.id} />
          <div className="variable-name">
            <input
              type="text"
              name={`variables|${i}|name`}
              placeholder={I18n.t('calculation_variable_name_placeholder', {scope: 'fe'})}
              className="form-control"
              defaultValue={v.name}
              autoComplete={this.getAutoComplete()}
              {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
            />
          </div>
          <div className="variable-component pl-1">
            {!v.formId ? (
              <select name={`variables|${i}|componentId`} className="form-control" defaultValue={`${v.componentId}${v.componentSuffix || ""}`} {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})} autoComplete={this.getAutoComplete()}>
                {this.variableComponentOptions().map((o) => (
                  <option key={o[0]} value={o[0]}>
                    {o[1]}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <input type="hidden" name={`variables|${i}|formId`} className="form-control" defaultValue={v.formId} />
                <input type="hidden" name={`variables|${i}|formEntity`} className="form-control" defaultValue={v.formEntity} />
                <input type="text" name={`variables|${i}|reportKey`} readOnly className="form-control" defaultValue={v.reportKey} />
              </>
            )}
          </div>
          <div className="variable-value pl-1">
            <Tooltip title={I18n.t('calculation_variable_value_help', {scope: 'fe'})}>
              <input
                type="text"
                name={`variables|${i}|value`}
                placeholder={I18n.t('calculation_variable_value_placeholder', {scope: 'fe'})}
                className="form-control"
                defaultValue={v.value}
                onChange={this.updateSettings}
                autoComplete={this.getAutoComplete()}
              />
            </Tooltip>
          </div>

          {this.allowModifyInSafeMode &&
            <div>
              <a href="#" className="delete icon-trash" title={I18n.t('delete_variable', {scope: 'fe'})} onClick={(e) => this.deleteVariable(e, i)}></a>
            </div>
          }
        </div>
      );
    });

    return (
      <div className="setting variables">
        <label>{I18n.t('field_variables', {scope: 'fe'})}</label>
        {showVariableNameWarning ? (
          <div className="settings-warning">
            <i className="fa fa-warning"></i>
            {" " + I18n.t('duplicate_variable_name_warning', {scope: 'fe'})}
          </div>
        ) : null}
        {variables}
        {this.allowModifyInSafeMode &&
          <Row>
            <Col size="xs-12">
              <Button elemType="button" onClick={this.addVariable}>
                <i className="icon-add"></i> {I18n.t('add_variable', {scope: 'fe'})}
              </Button>
              {this.props.addVariableUrl ? (
                <Button elemType="button" className="ml-1" onClick={() => this.addFormVariable(this.props.content.id)}>
                  <i className="icon-add"></i> {I18n.t('add_variable_from_form', {scope: 'fe'})}
                </Button>
              ) : null}
            </Col>
          </Row>
        }
      </div>
    );
  }

  renderStylingSettings() {
    const {id} = this.props.content;
    const style_options = this.getStyleOptions().map((v, i) => {
      return (
        <div key={'option-' + id + i} className="option" style={{position: 'relative'}}>
          {i > 0 &&
            <div className="row">
              <div className="delete" style={{position: 'absolute', right: 0, top: 0}}>
                <a href="#" className="icon-trash" title={I18n.t('delete_option', {scope: 'fe'})} onClick={(e) => this.deleteOption(e, i)}></a>
              </div>

              <div className="col-xs-6">
                <div className="form-group mb-1">
                  <label>{I18n.t('calculation_output', {scope: 'fe'})}</label>
                  <input
                    type="text"
                    name={`style_options|${i}|value`}
                    className="form-control"
                    defaultValue={v.value}
                    onChange={this.updateSettings}
                    autoComplete={this.getAutoComplete()}
                  />
                </div>
              </div>
            </div>
          }
          <div className="row">
            <div className="col-xs-6">
              <div className="form-group mb-1">
                <label>{I18n.t('color')}</label>
                <CommonColorPicker
                  name={`style_options|${i}|color`}
                  value={v.color || '#000'}
                  onChange={(v) => this.updateOptionSettings(i, 'color', v)}
                />
              </div>
            </div>
            <div className="col-xs-6">
              <div className="form-group mb-1">
                <label>{I18n.t('font_size')}</label>
                <CommonSlider
                  name={`style_options|${i}|font_size`}
                  value={v.font_size}
                  min={0}
                  max={70}
                  onChange={(v) => this.updateOptionSettings(i, 'font_size', v)}
                />
              </div>
            </div>
          </div>
          <hr className="mt-0 mb-1" style={{borderColor: '#ccc'}}/>
        </div>
      )
    })

    return (
      <div className="setting">
        <label>{I18n.t('styling', {scope: 'fe'})}</label>
        <p className='description'>{I18n.t('styling_description', {scope: 'fe'})}</p>
        <div className="option-groups">
          <div className="option-group">
            <div className="options">
              {style_options}
            </div>
            <div className="add-buttons">
              <Button elemType="button" onClick={this.addOption}>
                <i className="icon-add"></i> {I18n.t('add_option', {scope: 'fe'})}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  getVariables() {
    return (this.props.content || {}).variables || [];
  }

  variableComponentOptions() {
    let options = [[["", I18n.t('variable_report_key_placeholder', {scope: 'fe'})]]];

    if (this.props.findComponentsCallback) {
      [
        ...this.props.findComponentsCallback({
          dataType: [DataType.INTEGER, DataType.DECIMAL, DataType.STRING],
        }),
        ...this.props.findComponentsCallback({
          className: "AdaliaMEEvaluationScoreCounter",
        }),
        // Temporarily added for GCF Demo
        ...this.props.findComponentsCallback({
          className: "RepeatableRegion",
        }),
      ].forEach((c) => {
        if (c.id != this.props.content.id) {
          const fc = this.createNewComponentInstance(c, {});
          options.push(fc.getOptionsForComponentSelect());
        }
      });
    }
    return options.flat(1);
  }

  beforeUpdateSettings(content) {
    let variables = [];
    content.style_options = [];

    Object.keys(content)
      .filter((k) => k.match(/^variables\|/))
      .forEach((k) => {
        let parts = k.split("|"); // variables|index|attr
        variables[parts[1]] = variables[parts[1]] || {};

        if (parts[2] == "componentId") {
          if (content[k].match("_")) {
            variables[parts[1]][parts[2]] = content[k].split("_")[0];
            variables[parts[1]].componentSuffix = `_${content[k].split("_")[1]}`;
          } else {
            variables[parts[1]].componentSuffix = null;
            variables[parts[1]][parts[2]] = content[k];
          }
        } else {
          variables[parts[1]][parts[2]] = content[k];
        }


        delete content[k];
      });

    content.validations ??= {};
    if ((content.validateEqual || '').length > 0) {
      content.validations.equal = castDataType(content.validateEqual, content.dataType);
    }

    Object.keys(content).filter((k) => {
      return k.match(/^style_options\|/);
    }).forEach((k) => {
      let parts = k.split('|'); // style_options|index|attr
      if (parts[0] === 'style_options') {
        let oIndex = parseInt(parts[1], 10);
        content.style_options[oIndex] = content.style_options[oIndex] || {};
        content.style_options[oIndex][parts[2]] = (parts[2] === 'font_size') ? castDataType(content[k], DataType.BOOLEAN) : content[k];
      }
      delete content[k];
    });

    content.variables = variables;
    content.label_hidden = castDataType(content.label_hidden, DataType.BOOLEAN);
    this.beforeUpdateSettingsPrettyView(content);
  }

  addVariable = () => {
    const content = this.constructor.addVariable(this.props.content);
    this.props.updateSettingsCallback(content.id, content);
  };

  addFormVariable = (componentId) => {
    openModalDialog({
      title: I18n.t("add_variable"),
      modalClass: "modal-lg",
      hideButtons: true,
      url: `${this.props.addVariableUrl}?component_id=${componentId}`,
    });
  };

  deleteVariable = (e, i) => {
    e.preventDefault();

    if (confirm(I18n.t('confirm_delete_variable', {scope: 'fe'}))) {
      let content = update(this.props.content, {
        variables: {
          $splice: [[i, 1]],
        },
      });

      this.props.updateSettingsCallback(content.id, content);
    }
  };

  getStyleOptions() {
    return this.props.content?.style_options || [];
  };

  addOption = () => {
    let content = update(this.props.content, {
      style_options: (arr = []) => [
        ...arr,
        { font_size: 14, color: "#000", value: "" }
      ],
    });
    this.props.updateSettingsCallback(content.id, content);
  };

  deleteOption = (e, i) => {
    e.preventDefault();

    let content = update(this.props.content, {style_options: {$splice: [[i, 1]]}});
    this.props.updateSettingsCallback(content.id, content);
  };

  updateOptionSettings(idx, attr, value) {
    let style_options = this.getStyleOptions();
    style_options[idx][attr] = value;
    let content = {id: this.props.content.id, style_options: style_options};
    this.props.updateSettingsCallback(content.id, content, true);
  }
}

// Renders input with default value or calculated value
const CalculationValue = ({
  inputName,
  defaultValue,
  shouldDisplayDefaultValue,
  shouldCalculateValue,
  shouldFetchVariableValues,
  shouldDisplayError,
  calculationDelay = 0,
  formula,
  errorValue,
  variables,
  style_options,
  answers,
  prefillAnswers,
  repeatedFieldScope,
  onValueChange,
  findComponentsCallback,
  projectId,
  createNewComponentInstance,
  reportKeysToFieldsFunctions
}) => {
  // Get calculated value and loading state from hook.
  const [value, error, loading] = useCalculatedValue({
    shouldCalculateValue,
    shouldFetchVariableValues,
    formula,
    variables,
    answers,
    prefillAnswers,
    repeatedFieldScope,
    findComponentsCallback,
    projectId,
    createNewComponentInstance,
    reportKeysToFieldsFunctions
  });

  const [displayLoading, setDisplayLoading] = useState(false);
  const inputRef = React.useRef();
  const timeout = React.useRef();

  useEffect(() => {
    if (inputRef.current) {
      // Disable submits while the remote values are actually being loaded
      jQuery(inputRef.current).closest('form').find('button[type="submit"]').attr('disabled', loading);
    }
  }, [loading])


  useEffect(() => {
    if (shouldCalculateValue) {
      // Set loading indicator
      setDisplayLoading(true);
      // Updates answers after value is changed
      onValueChange(value);
      // Remove loading indicator after delay
      clearTimeout(timeout.current);
      timeout.current = setTimeout(() => setDisplayLoading(false), calculationDelay);
    }
  }, [value]);

  // Display default value, loading text, error (in EDIT mode), errorValue (in other modes), actual value, or empty string.
  let displayedValue;

  if (shouldDisplayDefaultValue) {
    displayedValue = defaultValue;
  } else if (loading || displayLoading) {
    displayedValue = I18n.t("loading");
  } else if (value || value === 0) {
    displayedValue = value;
  } else if (error) {
    displayedValue = shouldDisplayError ? error : errorValue;
  } else {
    displayedValue = "";
  }

  const matchedStyleConfig = (style_options[0] && (style_options.find(x => x.value === value) || style_options[0])) || {}
  const customStyle = {
    color: matchedStyleConfig.color,
    fontSize: matchedStyleConfig.font_size
  };

  return (
    <>
      <input
        ref={inputRef}
        // Hidden input which contains the calculated value
        // It will contain the value before the loading indicator is removed
        type="hidden"
        name={inputName}
        // Ensure that value is not null or undefined
        value={value || value === 0 ? value : ""}
      />
      {(loading || displayLoading) ?
        <i className="fa fa-spinner fa-spin"></i> :
        <input
          // Text input just for displaying value, error or loading text
          type="text"
          className="result form-control"
          // Ensure that value is not null or undefined
          value={displayedValue || displayedValue === 0 ? displayedValue : ""}
          readOnly={true}
          style={{
            borderLeft: '1px solid #ccc',
            paddingLeft: '8px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            ...(customStyle)
          }}
        />
      }
    </>
  );
};

const extractReportKeys = (components) => {
  let reportKeys = {};

  const traverse = (node) => {
    // Check if the current node has a reportKey property
    if (node?.hasOwnProperty('reportKey')) {
      reportKeys[node.reportKey] = node.name;
    }

    // If the current node is an object, loop through its keys
    for (let key in node) {
      if (node?.hasOwnProperty(key) && typeof node[key] === 'object') {
        // Recursive call for nested objects/arrays
        traverse(node[key]);
      }
    }
  };

  // Start the traversal from the root object
  for (let obj of components) {
    traverse(obj); 
  }

  return reportKeys;
};

const replaceReportKeysWithFields = (formula, findComponentsCallback, reportKeysToFieldsFunctions) => {
  const components = findComponentsCallback({
    className: "RepeatableRegion",
  });
  const fields = extractReportKeys(components);

  // Replace report keys in the formula with their corresponding field names ("report_key" -> "field_T8sY29N")
  let newFormula = formula;
  reportKeysToFieldsFunctions.forEach((funcName) => {
    const regex = new RegExp(`${funcName}\\(([^)]+)\\)`, "g");
    newFormula = newFormula.replace(regex, (match, p1) => {
      return match.replace(/"([^"]+)"/g, (m, key) => `"${fields[key] || key}"`);
    });
  });
  return newFormula;
}

const VALUE_ERROR = "#VALUE!";

// Calculates value from variables
const useCalculatedValue = ({
  shouldCalculateValue,
  shouldFetchVariableValues,
  formula: staticFormula,
  variables,
  answers,
  prefillAnswers,
  repeatedFieldScope,
  findComponentsCallback,
  projectId,
  createNewComponentInstance,
  reportKeysToFieldsFunctions
}) => {
  const fContext = useContext(FormContext);
  const [value, setValue] = useState();
  const [error, setError] = useState();
  const [variableValues, setVariableValues] = useState({});
  const [remoteVariableValues, loading] = useRemoteVariableValues(
    shouldFetchVariableValues,
    projectId,
    React.useMemo(() => variables.filter((v) => v.formId && v.reportKey), [variables])
  );

  const formula = React.useMemo(() => {
    if (!findComponentsCallback || reportKeysToFieldsFunctions.filter(f => staticFormula.includes(f)).length === 0) {
      return staticFormula;
    }
    return replaceReportKeysWithFields(staticFormula, findComponentsCallback, reportKeysToFieldsFunctions);
  }, [staticFormula])

  const checkVariableValuePresence = () => {
    let allPresent = true;

    variables.forEach((v) => {
      const value = variableValues[v.name];
      // Quick check to detect if variable name is found in the formula
      // Doesn't actually check if variable is used in formula
      if (!value && value !== 0 && formula.includes(v.name)) {
        allPresent = false;
        return;
      }
    });

    return allPresent;
  };

  const updateValue = () => {
    // // Merge remotely fetched values from other forms
    // Remote values take precedence over default values.
    const { error: parserError, result } = parseFormula(Parser, formula, variableValues);

    // Only update state if changed
    if (result != value) {
      setValue(result);
    }

    // Don't return value error if variable values are missing
    const allVariablesPresent = checkVariableValuePresence();
    let displayedError = !allVariablesPresent && parserError == VALUE_ERROR ? null : parserError;

    if (error != displayedError) {
      setError(displayedError);
    }
  };

  useEffect(() => {
    // Update value, when variable values change
    updateValue();
  }, [variableValues, formula, shouldCalculateValue]);

  const updateVariableValues = () => {
    // If remote values are fetched, merge with them
    // If not, start with empty values to ensure old default values won't be used
    let values = { ...(shouldFetchVariableValues ? variableValues : {}) };

    variables.forEach((v) => {
      if (v.componentId) {
        if (findComponentsCallback) {
          let c = findComponentsCallback({ id: v.componentId })[0];

          if (c) {
            // const fc = createNewComponentInstance(c, { answers, prefillAnswers });
            let cClass = Resolver.componentClassName(c.type);
            let props = {
              content: c,
              answers: answers,
              prefillAnswers: prefillAnswers,
              repeatedFieldScope: repeatedFieldScope
            };
            const fc = (new FormComponent[cClass](props))
            values[v.name] =
              typeof fc.getAggregateAnswer != "undefined"
                ? fc.getAggregateAnswer((v.componentSuffix || "").slice(1), fContext)
                : fc.getAnswer(fContext);
          }
        }
      }

      const value = values[v.name];
      // If value is empty, use default value, otherwise use answer.
      // If neither value nor default value is present, result will not be displayed
      if (!value && value !== 0) {
        values[v.name] = v.value;
      }
    });

    // If remote values are fetched, merge with them
    // If not, start with empty values to ensure old default values won't be used
    setVariableValues(
      update(shouldFetchVariableValues ? variableValues : {}, {
        $merge: values,
      })
    );
  };

  // Merge casted remote values to variableValues
  const mergeRemoteValues = () => {
    let values = {};

    Object.keys(remoteVariableValues).forEach((k) => {
      const { value, data_type } = remoteVariableValues[k];
      // Only merge if value is present
      if (value || value === 0) {
        values[k] = castDataType(value, data_type);
      }
    });

    setVariableValues(update(variableValues, { $merge: values }));
  };

  useEffect(() => {
    if (shouldFetchVariableValues) mergeRemoteValues();
  }, [remoteVariableValues]);

  useEffect(() => {
    if (shouldCalculateValue) updateVariableValues();
  }, [answers, variables, shouldCalculateValue]);

  return [value, error, loading];
};
